import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { Dictionary } from 'src/app/shared/models/main/dictionary'
import { DdcLoggerService } from '../log/ddc-logger.service'
import { ApiResponse } from '../state/library-state.service'
import { UserService } from '../user-info/user.service'

@Injectable({
  providedIn: 'root',
})
export class DictionaryService {
  private destroy$ = new Subject<void>()
  private _username: string = ''
  constructor(
    private http: HttpClient,
    private user_info: UserService,
    private logger: DdcLoggerService,
  ) {
    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.username) {
        this._username = res.username
      }
    })
  }

  get_dics(is_simple: boolean = false): any {
    this.logger.info('DIC | get_dics')
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    var request_arg = ''
    if (is_simple) request_arg = '?is_simple=true'

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.DIC}?username=${this._username}&${request_arg}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res.results
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | get_dics | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  get_dic(dic_id: number): any {
    this.logger.info('DIC | get_dic | dic_id: ' + dic_id)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http.get(`${Backend.HOST_CONTAINER + Backend.DIC}${dic_id}/`, httpOptions).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('DIC | get_dic | catchError | errorMessage: ' + errorMessage)
        return of(errorMessage)
      }),
    )
  }

  get_searched_dics(target: string, maxRows: number = 10): any {
    this.logger.info('DIC | get_searched_dics | target: ' + target + ' | maxRows: ' + maxRows)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    // limit max rows to 10
    if (maxRows > 10 || maxRows < 1) maxRows = 10

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.DIC}search/?max=${maxRows}&title=${target}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | get_searched_dics | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  get_user_dic(dic_id: number, username: string): any {
    this.logger.info('DIC | get_user_dic | dic_id: ' + dic_id + ' | username: ' + username)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(`${Backend.HOST_CONTAINER + Backend.DIC}${dic_id}/?username=${username}`, httpOptions)
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | get_user_dic | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  get_dics_page(page: number, pageSize: number): Observable<ApiResponse<Dictionary>> {
    this.logger.info('DIC | get_dics_page | page: ' + page)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get<ApiResponse<Dictionary>>(
        `${Backend.HOST_CONTAINER + Backend.DIC}?page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | get_dics_page | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  get_user_dics_page(username: string, page: number, pageSize: number): any {
    this.logger.info('DIC | get_user_dics_page | username: ' + username + ' | page: ' + page)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.DIC}?username=${username}&page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | get_user_dics_page | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  add_dic(element: any): any {
    this.logger.info('DIC | add_dic | element: ' + element)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      title: element.title,
      description: element.description,
    }

    return this.http.post(`${Backend.HOST_CONTAINER + Backend.DIC}`, userData, httpOptions).pipe(
      map((res: any) => {
        return { success: true, res: res }
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('DIC | add_dic | catchError | errorMessage: ' + errorMessage)
        return of({ success: false, res: errorMessage })
      }),
    )
  }

  add_words_to_dic(dic_id: number, word_ids: any): any {
    this.logger.info('DIC | add_words_to_dic | dic_id: ' + dic_id + ' | word_ids: ' + word_ids)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      words: word_ids,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.DIC + dic_id + '/' + Backend.ADD_WORDS_TO_DIC}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | add_words_to_dic | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  add_pairs_to_dic(dic_id: number, pair_ids: any): any {
    this.logger.info('DIC | add_pairs_to_dic | dic_id: ' + dic_id + ' | pair_ids: ' + pair_ids)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      pairs: pair_ids,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.DIC + dic_id + '/' + Backend.ADD_PAIRS_TO_DIC}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | add_pairs_to_dic | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  edit_dic(dic_data: any): any {
    this.logger.info('DIC | edit_dic | dic_data: ' + dic_data)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .patch(`${Backend.HOST_CONTAINER + Backend.DIC + dic_data.id + '/'}`, dic_data, httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | edit_dic | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  edit_dics_of_word(word_id: number, selected_dics: any): any {
    this.logger.info(
      'DIC | edit_dics_of_word | word_id: ' + word_id + ' | selected_dics: ' + selected_dics,
    )
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      selected_dics: selected_dics,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.WORD + word_id + '/' + Backend.EDIT_DICS}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | edit_dics_of_word | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  edit_dics_of_pair(pair_id: number, selected_dics: any): any {
    this.logger.info(
      'DIC | edit_dics_of_pair | pair_id: ' + pair_id + ' | selected_dics: ' + selected_dics,
    )
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      selected_dics: selected_dics,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.PAIR + pair_id + '/' + Backend.EDIT_DICS}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | edit_dics_of_pair | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  delete_dic(dic_id: number): any {
    this.logger.info('DIC | delete_dic | dic_id: ' + dic_id)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .delete(`${Backend.HOST_CONTAINER + Backend.DIC + dic_id + '/'}`, httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('DIC | delete_dic | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  remove_words_from_dic(dic_id: number, word_ids: any): any {
    this.logger.info('DIC | remove_words_from_dic | dic_id: ' + dic_id + ' | word_ids: ' + word_ids)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      words: word_ids,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.DIC + dic_id + '/' + Backend.REMOVE_WORDS_FROM_DIC}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'DIC | remove_words_from_dic | catchError | errorMessage: ' + errorMessage,
          )
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  remove_pairs_from_dic(dic_id: number, pair_ids: any): any {
    this.logger.info('DIC | remove_pairs_from_dic | dic_id: ' + dic_id + ' | pair_ids: ' + pair_ids)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      pairs: pair_ids,
    }

    return this.http
      .post(
        `${Backend.HOST_CONTAINER + Backend.DIC + dic_id + '/' + Backend.REMOVE_PAIRS_FROM_DIC}`,
        userData,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'DIC | remove_pairs_from_dic | catchError | errorMessage: ' + errorMessage,
          )
          return of({ success: false, res: errorMessage })
        }),
      )
  }
}
